import React, { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import FormInputComponent from "@via/components/inputs/form-input-component.tsx";
import { userInvitationOptions } from "@via/utils/form-validators.tsx";
import {
  useApprovePendingRegistration,
  useCancelInvitation,
  useGetOrganizationPendingUsers,
  useGetOrganizationUserRoles,
  useGetOrganizationUsers,
  useInviteUserToOrganization,
  useInviteUserToOrganizationWithBilling,
  useRemoveUserFromOrganization,
  useUpdateInvitation,
  useUpdateOrganizationUser,
} from "@via/pages/organization/organization.api.tsx";
import {
  classNames,
  getReadableOrganizationRoleByRoleName,
  UserOrganizationRoleEnum,
} from "@via/utils/helpers.tsx";
import {
  CheckCircleIcon,
  TrashIcon,
  UserIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "@via/hooks/useAuth.tsx";
import { organizationMembershipFor } from "@via/hooks/organization-access.ts";
import CapacityBar from "@via/components/progress-bar/capacity-bar.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import DeleteModal from "@via/components/delete-modal/delete-modal.tsx";
import { InvitationType, MemberSource, OrganizationUser } from "common";
import PlanLimitNotice from "@via/pages/organization/plan-limit-notice.tsx";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext.tsx";
import { ROUTES } from "@via/utils/constants";

interface Card {
  title: string;
  description: string;
}

const cards: Card[] = [
  {
    title: "Members",
    description:
      "Regular usage, job submission, data interrogation, and credit balance visibility.",
  },
  // {
  //   title: "Collaborators",
  //   description:
  //     "View-only access to runs, job statuses, and visualization. On the Org’s billing plan.",
  // },
];

const isLoggedInUser = (
  loggedInUserEmail: string | null,
  targetUserEmail: string
) => {
  if (loggedInUserEmail === targetUserEmail) {
    return "(You)";
  }
};

const getMemberRoleId = organizationUserRoles => {
  const memberRole = (organizationUserRoles || []).find(
    role => role.name === UserOrganizationRoleEnum.Member
  );

  return memberRole ? memberRole.id : organizationUserRoles[0].id;
};

interface RoleOption {
  label: string;
  value: string;
  name: string;
  description: string;
  optionId: string | number;
  userOrganizationId?: string;
}

function mapRolesToOptions(
  roles: any,
  optionId: string | number,
  userOrganizationId?: string
): RoleOption[] {
  return (roles || []).map(role => ({
    label: getReadableOrganizationRoleByRoleName(role.name) || "",
    value: role.id || "",
    name: role.name || "",
    description: role.description || "",
    optionId,
    userOrganizationId,
  }));
}

interface UserInvitationProps {
  organizationId: string;
}

const UserInvitation: React.FC<UserInvitationProps> = ({ organizationId }) => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const approveInvitationAttemptedRef = useRef(false);

  const [showExtraUserInvitation, setShowExtraUserInvitation] = useState(false);

  // The organization being managed, not the first membership: its roles and its capacity.
  const membership = organizationMembershipFor(user, organizationId);
  const capacity = membership?.capacity || 5;

  const { data: organizationUserRoles } =
    useGetOrganizationUserRoles(organizationId);

  const { data: organizationUsers, isSuccess: isOrganizationUsersSuccess } =
    useGetOrganizationUsers({ id: organizationId });
  const sortedOrganizationUsers = [...(organizationUsers || [])].sort(
    (a, b) => {
      const aIsCurrent = a.email === user.email;
      const bIsCurrent = b.email === user.email;
      return aIsCurrent === bIsCurrent ? 0 : aIsCurrent ? -1 : 1;
    }
  );

  const {
    data: organizationPendingUserInvites,
    isSuccess: isOrganizationPendingUsersSuccess,
  } = useGetOrganizationPendingUsers({ id: organizationId });

  const capacityConsumingUsers =
    organizationUsers?.filter(
      u => u.memberSource !== MemberSource.URL_SIGNUP
    ) ?? [];
  const capacityConsumingInvites =
    organizationPendingUserInvites?.filter(
      i => i.invitationType !== InvitationType.URL_SELF_SIGN
    ) ?? [];
  const currentOrganizationNumber =
    capacityConsumingUsers.length + capacityConsumingInvites.length;
  const isOrganizationFull = currentOrganizationNumber >= capacity;

  const getUsersContext = useCallback(() => {
    const adminCount =
      organizationUsers?.filter(
        user => user.userOrganizationRole === "org_admin"
      ).length || 0;
    const memberCount =
      organizationUsers?.filter(user => user.userOrganizationRole === "member")
        .length || 0;

    return {
      organizationId,
      capacity: {
        current: currentOrganizationNumber,
        max: capacity,
        isFull: isOrganizationFull,
      },
      summary: {
        totalUsers: organizationUsers?.length || 0,
        totalPendingInvites: organizationPendingUserInvites?.length || 0,
        adminCount,
        memberCount,
      },
      users: sortedOrganizationUsers.map(orgUser => ({
        userId: orgUser.userId,
        email: orgUser.email,
        firstName: orgUser.firstName,
        lastName: orgUser.lastName,
        role: orgUser.userOrganizationRole,
        userOrganizationId: orgUser.userOrganizationId,
        isCurrentUser: orgUser.email === user?.email,
      })),
      pendingInvites:
        organizationPendingUserInvites?.map(invite => ({
          id: invite.id,
          email: invite.invitedEmail,
          roleId: invite.roleId,
          createdAt: invite.createdAt,
        })) || [],
      availableRoles:
        organizationUserRoles?.map(role => ({
          id: role.id,
          name: role.name,
          description: role.description,
        })) || [],
    };
  }, [
    organizationId,
    organizationUsers,
    sortedOrganizationUsers,
    organizationPendingUserInvites,
    organizationUserRoles,
    currentOrganizationNumber,
    isOrganizationFull,
    user,
  ]);

  useRegisterCortexContext({
    routePattern: ROUTES.ORGANIZATION_USERS,
    contextName: "Organization Users",
    getContext: getUsersContext,
    systemPrompt:
      "You are analyzing organization users and invitations page. Help answer questions about organization members, their roles, pending invitations, capacity limits, and user management.",
    enabled:
      !!organizationId &&
      (isOrganizationUsersSuccess || isOrganizationPendingUsersSuccess),
  });

  const {
    mutate: inviteUser,
    data: inviteUserResponse,
    isSuccess: isInviteUserSuccess,
    isPending: isInviteUserLoading,
  } = useInviteUserToOrganization();

  const {
    mutate: inviteUserWithBilling,
    error: inviteUserWithBillingError,
    isPending: isInviteUserWithBillingLoading,
  } = useInviteUserToOrganizationWithBilling();

  const { mutate: cancelInvitation } = useCancelInvitation();

  const {
    mutate: approvePendingRegistration,
    isPending: isApprovePendingRegistrationLoading,
    isSuccess: isApprovePendingRegistrationSuccess,
    isError: isApprovePendingRegistrationError,
  } = useApprovePendingRegistration();

  const approveInvitationId = searchParams.get("approveInvitation");

  useEffect(() => {
    if (
      !approveInvitationId ||
      !organizationUsers ||
      !user ||
      approveInvitationAttemptedRef.current
    ) {
      return;
    }
    const currentUserInOrg = organizationUsers.find(
      orgUser => orgUser.email === user.email
    );
    const isOrgAdmin =
      currentUserInOrg?.userOrganizationRole ===
      UserOrganizationRoleEnum.OrgAdmin;

    if (!isOrgAdmin) {
      return;
    }

    approveInvitationAttemptedRef.current = true;
    approvePendingRegistration({
      organizationId,
      invitationId: approveInvitationId,
    });
  }, [
    approveInvitationId,
    organizationId,
    organizationUsers,
    user,
    approvePendingRegistration,
  ]);

  useEffect(() => {
    if (isApprovePendingRegistrationSuccess) {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.delete("approveInvitation");
          return next;
        },
        { replace: true }
      );
    }
  }, [isApprovePendingRegistrationSuccess, setSearchParams]);

  useEffect(() => {
    if (isApprovePendingRegistrationError) {
      approveInvitationAttemptedRef.current = false;
    }
  }, [isApprovePendingRegistrationError]);

  const handleInvitationCancel = (
    organizationId: string,
    invitationId: string
  ) => {
    cancelInvitation({ organizationId, invitationId });
  };

  const [userToBeDeleted, setUserToBeDeleted] =
    useState<OrganizationUser | null>(null);

  const [adminToBeMember, setAdminToBeMember] = useState<RoleOption | null>(
    null
  );

  const {
    mutateAsync: removeUserFromOrganization,
    isPending: isRemoveUserFromOrganizationLoading,
  } = useRemoveUserFromOrganization();

  const handleUserDelete = async (
    organizationId: string,
    userId: number | undefined
  ) => {
    if (userId) {
      await removeUserFromOrganization({
        organizationId,
        userId,
      });
    }
  };

  const { mutate: updateInvitation, isPending: isUpdateInvitationLoading } =
    useUpdateInvitation();

  const updatePendingUserRole = (roleOption: RoleOption) => {
    updateInvitation({
      requestParams: { id: organizationId },
      requestBody: {
        roleId: roleOption.value,
        invitationId: roleOption.optionId.toString(),
      },
    });
  };

  const {
    mutate: updateOrganizationUser,
    isPending: isUpdateOrganizationUserLoading,
  } = useUpdateOrganizationUser();

  const updateOrganizationUserRole = (roleOption: RoleOption) => {
    const orgAdmins = organizationUsers?.filter(
      user => user.userOrganizationRole === "org_admin"
    );

    const isMultipleAdmins = orgAdmins && orgAdmins?.length > 1;
    const isSelfOption = roleOption.optionId === user?.id;
    const isChangingFromAdmin = roleOption.name !== "org_admin";

    if (isMultipleAdmins && isSelfOption && isChangingFromAdmin) {
      setAdminToBeMember(roleOption);
    } else {
      updateOrganizationUser({
        requestParams: { id: organizationId },
        requestBody: {
          roleId: roleOption.value,
          userOrganizationId: roleOption.userOrganizationId || "",
          userId: Number(roleOption.optionId),
        },
      });
    }
  };

  const { handleSubmit, control, reset, watch } = useForm<{ email: string }>({
    mode: "onChange",
    shouldUnregister: true,
  });
  const emailValue = watch("email");

  useEffect(() => {
    if (isInviteUserSuccess) {
      if (inviteUserResponse?.monthlyCost) {
        setShowExtraUserInvitation(true);
      } else {
        reset({
          email: "",
        });
        setShowExtraUserInvitation(false);
      }
    }
  }, [isInviteUserSuccess, inviteUserResponse]);

  const handleFormSubmit = async (payload: any) => {
    if (organizationUserRoles) {
      inviteUser({
        requestParams: { id: organizationId },
        requestBody: {
          email: payload.email,
          roleId: getMemberRoleId(organizationUserRoles),
        },
      });
    }
  };

  const handleExtraUserInvite = () => {
    if (organizationUserRoles) {
      inviteUserWithBilling({
        queryParams: { billingConfirmed: true },
        requestParams: { id: organizationId },
        requestBody: {
          email: emailValue,
          roleId: getMemberRoleId(organizationUserRoles),
        },
      });
    }
  };

  const isApprovingFromEmail =
    !!approveInvitationId && isApprovePendingRegistrationLoading;

  return (
    <div className="relative">
      {isApprovingFromEmail ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/80">
          <p className="text-sm font-medium text-gray-700">
            Approving registration...
          </p>
        </div>
      ) : null}
      {organizationUsers && organizationPendingUserInvites ? (
        <CapacityBar current={currentOrganizationNumber} capacity={capacity} />
      ) : null}

      <form
        className="flex flex-col gap-4 h-full mt-4"
        onSubmit={e => {
          e.preventDefault();
          void handleSubmit(handleFormSubmit)(e);
        }}>
        <div className="flex gap-2">
          <div className="w-full">
            <FormInputComponent
              control={control}
              name="email"
              label=""
              autoComplete="off"
              placeholder="Enter email address"
              validations={userInvitationOptions.email}
              className="py-2 ring-indigo-300 ring-2"
            />
          </div>
          <button
            id="add-member"
            type="submit"
            className={
              "mt-2 bg-indigo-600 text-white hover:bg-indigo-500 hover:shadow-md rounded-md h-[40px] w-[120px] text-sm shadow-sm duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 cursor-pointer"
            }>
            Add Member
          </button>
        </div>
      </form>

      {isOrganizationFull && showExtraUserInvitation ? (
        <PlanLimitNotice
          billingInfo={inviteUserResponse}
          inviteUserError={inviteUserWithBillingError}
          onAgreeExtraUser={handleExtraUserInvite}
          isLoading={isInviteUserLoading || isInviteUserWithBillingLoading}
        />
      ) : null}

      {(isOrganizationUsersSuccess && organizationUsers.length > 0) ||
      (isOrganizationPendingUsersSuccess &&
        organizationPendingUserInvites &&
        organizationPendingUserInvites.length > 0) ? (
        <div className="mt-8 grid gap-y-5">
          {sortedOrganizationUsers.map(organizationUser => (
            <div
              className="grid gap-x-10 items-center"
              style={{ gridTemplateColumns: "1fr 165px 190px 140px" }}
              key={`user-${organizationUser.userId}`}>
              <p className="flex items-center capitalize min-w-0">
                <UserIcon className="mr-4 h-5 w-5 shrink-0" />
                <span className="truncate">
                  {organizationUser.firstName} {organizationUser.lastName}{" "}
                  {isLoggedInUser(organizationUser.email, user.email)}
                </span>
              </p>
              <span className="px-3 py-1 text-sm font-semibold border-1 border-gray-300 rounded-xl w-fit">
                Active
              </span>
              <div className="min-w-0">
                <SelectComponent
                  key={"organization-user-role-" + organizationUser.userId}
                  name="organization-user-role"
                  isLoading={isUpdateOrganizationUserLoading}
                  options={mapRolesToOptions(
                    organizationUserRoles,
                    organizationUser.userId,
                    organizationUser.userOrganizationId
                  )}
                  value={mapRolesToOptions(
                    organizationUserRoles,
                    organizationUser.userId,
                    organizationUser.userOrganizationId
                  ).find(
                    ({ name }) => name === organizationUser.userOrganizationRole
                  )}
                  onChange={value =>
                    updateOrganizationUserRole(value as RoleOption)
                  }
                  enableClear={false}
                  readOnly={false}
                />
              </div>
              <div className="flex gap-2 items-center justify-end">
                <button
                  disabled={organizationUser.email === user.email}
                  onClick={() =>
                    organizationUser.email !== user.email
                      ? setUserToBeDeleted(organizationUser)
                      : null
                  }
                  className={classNames(
                    "rounded-md p-1.5",
                    organizationUser.email !== user.email
                      ? "hover:bg-gray-100"
                      : ""
                  )}>
                  <TrashIcon
                    className={classNames(
                      organizationUser.email === user.email
                        ? "text-gray-300"
                        : "text-gray-600",
                      "h-5 w-5"
                    )}
                  />
                </button>
              </div>
            </div>
          ))}
          {(organizationPendingUserInvites ?? []).map(invite => (
            <div
              className="grid gap-x-10 items-center"
              style={{ gridTemplateColumns: "1fr 165px 190px 140px" }}
              key={`invite-${invite.id}`}>
              <p className="flex items-center min-w-0">
                <UserPlusIcon className="mr-4 h-5 w-5 shrink-0" />
                <span className="truncate">{invite.invitedEmail}</span>
              </p>
              <span className="px-3 py-1 text-sm font-semibold border-1 border-gray-300 rounded-xl w-fit">
                {invite.invitationType === InvitationType.URL_SELF_SIGN
                  ? "Pending Admin"
                  : "Pending User"}
              </span>
              <div className="min-w-0">
                <SelectComponent
                  key={"pending-user-role-" + invite.id}
                  name="pending-user-role"
                  isLoading={isUpdateInvitationLoading}
                  options={mapRolesToOptions(organizationUserRoles, invite.id)}
                  value={mapRolesToOptions(
                    organizationUserRoles,
                    invite.id
                  ).find(({ value }) => value === invite.roleId)}
                  onChange={value => updatePendingUserRole(value as RoleOption)}
                  enableClear={false}
                  readOnly={
                    invite.invitationType === InvitationType.URL_SELF_SIGN
                  }
                />
              </div>
              <div className="flex gap-2 items-center justify-end">
                {invite.invitationType === InvitationType.URL_SELF_SIGN ? (
                  <button
                    type="button"
                    disabled={isApprovePendingRegistrationLoading}
                    className="inline-flex items-center justify-center rounded-md p-1.5 bg-indigo-600 text-white hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
                    onClick={() =>
                      approvePendingRegistration({
                        organizationId,
                        invitationId: invite.id,
                      })
                    }>
                    <CheckCircleIcon className="h-5 w-5" />
                  </button>
                ) : null}
                <button
                  onClick={() =>
                    handleInvitationCancel(organizationId, invite.id)
                  }
                  className="rounded-md p-1.5 hover:bg-gray-100">
                  <TrashIcon className="h-5 w-5 text-gray-600" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {adminToBeMember && (
        <DeleteModal
          item={adminToBeMember}
          setItem={setAdminToBeMember}
          deleteFunction={async () =>
            await updateOrganizationUser({
              requestParams: { id: organizationId },
              requestBody: {
                roleId: adminToBeMember.value,
                userOrganizationId: adminToBeMember.userOrganizationId || "",
                userId: Number(adminToBeMember.optionId),
              },
            })
          }
          isLoading={isUpdateOrganizationUserLoading}
          message={() => (
            <>
              Are you sure you want to change your role to{" "}
              <strong className="font-semibold">
                {adminToBeMember?.label ?? "Member"}
              </strong>
              {". "}
              You will lose admin privileges and will not be able to manage
              organization settings.
            </>
          )}
          confirmText="Downgrade to Member"
          title="Change Admin to Member"
        />
      )}

      {userToBeDeleted && (
        <DeleteModal
          item={userToBeDeleted}
          setItem={setUserToBeDeleted}
          deleteFunction={() =>
            handleUserDelete(organizationId, userToBeDeleted?.userId)
          }
          isLoading={isRemoveUserFromOrganizationLoading}
          message={name => (
            <>
              Are you sure you want to remove{" "}
              <strong className="font-semibold">
                {userToBeDeleted?.username ?? "User"}
              </strong>{" "}
              from the organization? This user will lose access to all
              organization resources.
              {name ?? ""}
            </>
          )}
          title="Remove User from Organization"
        />
      )}

      {organizationPendingUserInvites?.length === 0 ? (
        <div className="mt-10">
          <div className="space-y-2">
            <p className="text-gray-500">You can invite</p>
            <div className="flex gap-4">
              {cards.map(card => (
                <div
                  key={card.title}
                  className="flex-1 rounded-lg bg-gray-50 p-6 text-center">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {card.title}
                  </h3>
                  <p className="mt-2 text-sm text-gray-600">
                    {card.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default UserInvitation;
